import { useState } from 'react';
import { Link } from 'react-router-dom';
import { ChevronLeft, ChevronRight, Trophy } from 'lucide-react';
import type { CompStage, MatchSummary } from '../lib/api';
import { StatusBadge } from './StatusBadge';
import { divisionLetter } from '../lib/groupMatchesByStage';
import { clanLabelFrom } from '../lib/displayName';
import { cn } from '../lib/cn';
import { MORPH } from '../lib/motion';

// ─────────────────────────────────────────────────────────────────────────────
// DivisionFixturesCarousel — Matches page redesign (Lisa, 2026-07-19, from a
// mockup): a DIVISIONS stage's fixtures used to render as ONE flat "Week 3 /
// Week 2 / Week 1" list with every division merged together (see
// groupMatchesByStage.ts's groupByDivisionWeek, still used by Setup's
// MatchScheduleBoard — untouched by this file). Lisa found that not obvious.
// This replaces it, for DIVISIONS stages ONLY, with one block PER DIVISION,
// stacked down the page, each showing a SINGLE week's fixtures at a time
// behind ◀ / ▶ arrows — "who you played" (back) / future weeks (forward).
//
// Deliberately NOT the DivisionsScoreboardPanel/WeekPager idiom verbatim
// (git show 625baa0:frontend/src/components/DivisionsScoreboardPanel.tsx) —
// that panel bundles inline result-entry (ResultEntryForm) and date-agreement
// controls, which belong to the admin/captain scoreboard surface, not this
// read-mostly board (same "link out to the match card, don't duplicate
// scoring here" rule Matches.tsx's MatchLinkRow already follows). What IS
// reused is the ◀ ▶ pager idiom and the per-division "own viewedWeek state"
// shape — adapted here with NO date range and NO "Round N" fallback label
// (Lisa: "i don't think dates are needed, just the teams to play each
// other" — weeks are always "Week N", nothing else).
//
// Grouping is done HERE, independently, straight off `matches` (the
// DIVISIONS stage's own MatchSummary rows) — NOT off groupMatchesByStage's
// week-merged output, which deliberately merges every division into one
// group and is still the shape Setup's board wants untouched.
// ─────────────────────────────────────────────────────────────────────────────

// Week-grouping math — REPLICATED from src/shared/divisionSchedule.ts's
// weekForRound/currentWeek (backend, pure, no AWS imports), not imported: the
// frontend is a fully separate TypeScript/Vite project with no workspace
// wiring back to the backend's src/shared — see groupMatchesByStage.ts's own
// copy of this exact same math for the established precedent. Kept tiny and
// verbatim so it's trivial to eyeball against the source if that ever
// changes. `calendarCurrentWeek` is used ONLY to pick a sensible default
// viewed week below — its result never reaches the page as a date.
const DEFAULT_CADENCE_DAYS = 7;
const MS_PER_DAY = 24 * 60 * 60 * 1000;

function weekForRound(round: number, roundsPerWeek: number): number {
  const perWeek = Math.max(1, Math.floor(roundsPerWeek));
  return Math.ceil(round / perWeek);
}

function calendarCurrentWeek(scheduleStartAt: number, now: number, cadenceDays = DEFAULT_CADENCE_DAYS): number {
  if (now < scheduleStartAt) return 0;
  const cadenceMs = cadenceDays * MS_PER_DAY;
  return Math.floor((now - scheduleStartAt) / cadenceMs) + 1;
}

export function DivisionFixturesCarousel({
  stage,
  matches,
  compId,
}: {
  stage: CompStage;
  matches: MatchSummary[];
  compId: string;
}) {
  // Same soft-guide read groupByDivisionWeek uses (default 1 round/week).
  const gamesPerWeek = Math.max(1, Math.floor(Number(stage.config?.gamesPerWeek) || 1));
  // Same anchor precedence as the backend's schedule overlay — an admin's
  // plannedStartAt estimate wins over startedAt, the stage's actual go-live.
  // null => no schedule anchor yet => no calendar-based default below.
  const scheduleStartAt = stage.plannedStartAt ?? stage.startedAt ?? null;

  const divisionsMap = new Map<number, Map<number, MatchSummary[]>>();
  for (const m of matches) {
    const groupIndex = m.groupIndex ?? 0;
    const week = weekForRound(m.groupRound ?? 0, gamesPerWeek);
    const weeks = divisionsMap.get(groupIndex) ?? new Map<number, MatchSummary[]>();
    const weekMatches = weeks.get(week) ?? [];
    weekMatches.push(m);
    weeks.set(week, weekMatches);
    divisionsMap.set(groupIndex, weeks);
  }

  const divisions = [...divisionsMap.entries()].sort(([a], [b]) => a - b);
  if (divisions.length === 0) return null;

  // Computed once for every division block — a schedule anchor is stage-wide,
  // not per-division.
  const calendarWeek = scheduleStartAt != null ? calendarCurrentWeek(scheduleStartAt, Date.now()) : null;

  return (
    <div className="space-y-3">
      {divisions.map(([groupIndex, weekMap]) => (
        <DivisionBlock key={groupIndex} compId={compId} groupIndex={groupIndex} weekMap={weekMap} calendarWeek={calendarWeek} />
      ))}
    </div>
  );
}

// One division's own block: header (division label + week pager) + that
// week's fixture rows. Its own `viewedWeek` hook — extracted into its own
// component so the divisions `.map()` above never puts a hook in a loop
// (same idiom StagePipelineCard/DivisionCard already use for their own list
// renders).
function DivisionBlock({
  compId,
  groupIndex,
  weekMap,
  calendarWeek,
}: {
  compId: string;
  groupIndex: number;
  weekMap: Map<number, MatchSummary[]>;
  calendarWeek: number | null;
}) {
  const orderedWeeks = [...weekMap.keys()].sort((a, b) => a - b);
  const minWeek = orderedWeeks[0];
  const maxWeek = orderedWeeks[orderedWeeks.length - 1];
  const clampWeek = (w: number) => Math.min(Math.max(w, minWeek), maxWeek);

  // Default viewed week: a schedule anchor's calendar-current-week wins
  // (clamped into this division's own available range — it's stage-wide math
  // and may fall outside what THIS division actually has fixtures for). No
  // anchor => earliest week that still has an unplayed (non-CONFIRMED)
  // fixture in THIS division, else the division's last week. Computed once,
  // at mount, via useState's lazy initializer.
  const [viewedWeek, setViewedWeek] = useState<number>(() => {
    if (calendarWeek != null) return clampWeek(calendarWeek);
    for (const w of orderedWeeks) {
      if ((weekMap.get(w) ?? []).some(m => m.status !== 'CONFIRMED')) return w;
    }
    return maxWeek;
  });

  const viewedIdx = Math.max(0, orderedWeeks.indexOf(viewedWeek));
  const hasPrev = viewedIdx > 0;
  const hasNext = viewedIdx < orderedWeeks.length - 1;
  const isCurrent = calendarWeek != null && viewedWeek === calendarWeek;

  const viewedMatches = [...(weekMap.get(viewedWeek) ?? [])].sort((a, b) => (a.groupRound ?? 0) - (b.groupRound ?? 0));

  const arrowCls =
    'inline-flex h-6 w-6 items-center justify-center rounded-md text-accent-500 transition hover:bg-accent-500/10 disabled:cursor-default disabled:text-ink-meta disabled:opacity-40 disabled:hover:bg-transparent';

  return (
    <div className="overflow-hidden rounded-lg bg-white/[0.03]">
      <div className="flex items-center justify-between gap-2 border-b border-white/[0.06] px-4 py-2.5">
        <span className="truncate font-display text-sm font-black uppercase tracking-wider text-white">
          Division {divisionLetter(groupIndex)}
        </span>
        <div className="flex shrink-0 items-center gap-1.5">
          <button
            type="button"
            onClick={() => { if (hasPrev) setViewedWeek(orderedWeeks[viewedIdx - 1]); }}
            disabled={!hasPrev}
            aria-label="Previous week"
            className={arrowCls}
          >
            <ChevronLeft className="h-4 w-4" />
          </button>
          <span className="flex min-w-[64px] items-center justify-center gap-1.5 text-center text-xs font-semibold text-ink-primary">
            Week {viewedWeek}
            {isCurrent && (
              <span className="rounded-full bg-accent-500/15 px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wide text-accent-500">
                Now
              </span>
            )}
          </span>
          <button
            type="button"
            onClick={() => { if (hasNext) setViewedWeek(orderedWeeks[viewedIdx + 1]); }}
            disabled={!hasNext}
            aria-label="Next week"
            className={arrowCls}
          >
            <ChevronRight className="h-4 w-4" />
          </button>
        </div>
      </div>

      <div className="space-y-2 px-3 py-2.5">
        {viewedMatches.length === 0 ? (
          <p className="px-1.5 text-xs text-ink-meta">No fixtures this week.</p>
        ) : (
          viewedMatches.map(m => <FixtureRow key={m.sk} m={m} compId={compId} />)
        )}
      </div>
    </div>
  );
}

// One fixture: teams + status/result + a link into the match card. Same
// idiom as Matches.tsx's MatchLinkRow, minus the date (this board never
// renders one) and minus the division tag (the whole block above is already
// scoped to one division).
function FixtureRow({ m, compId }: { m: MatchSummary; compId: string }) {
  const isCompleted = m.status === 'CONFIRMED' && m.nightWinner;
  const clan1Label = clanLabelFrom(m.clan1DisplayName, m.clan1);
  const clan2Label = clanLabelFrom(m.clan2DisplayName, m.clan2);
  const winnerLabel = m.nightWinner
    ? (m.nightWinner === m.clan1 ? clan1Label : m.nightWinner === m.clan2 ? clan2Label : clanLabelFrom(undefined, m.nightWinner))
    : null;

  return (
    <Link
      to={`/matchcard/${encodeURIComponent(m.sk)}?compId=${encodeURIComponent(compId)}`}
      className={cn('plate-sm group flex flex-col gap-2 px-4 py-3 sm:flex-row sm:items-center sm:justify-between sm:gap-3', MORPH)}
    >
      <span className="flex min-w-0 items-center gap-2">
        <span className="min-w-[60px] flex-1 truncate font-display text-sm font-black uppercase tracking-wider text-white sm:min-w-0 sm:flex-none">
          {clan1Label}
        </span>
        <span className="shrink-0 font-mono text-[10px] font-bold uppercase tracking-widest text-ink-meta">vs</span>
        <span className="min-w-[60px] flex-1 truncate font-display text-sm font-black uppercase tracking-wider text-white sm:min-w-0 sm:flex-none">
          {clan2Label}
        </span>
      </span>

      <span className="flex shrink-0 items-center gap-2">
        {winnerLabel && (
          <span className="hidden items-center gap-1.5 font-mono text-[10px] font-bold uppercase tracking-widest text-accent-500 sm:flex">
            <Trophy className="h-3 w-3" strokeWidth={3} />
            {winnerLabel}
          </span>
        )}
        {isCompleted ? <StatusBadge status="COMPLETED" /> : <StatusBadge status={m.status} />}
        <ChevronRight className="h-4 w-4 text-ink-meta transition-colors group-hover:text-ink-secondary" />
      </span>
    </Link>
  );
}
